import { z } from 'zod'
import { TreeNodeSchema } from '@/types/schemas/tree-node'
import type { TreeNode } from '@/types/schemas/tree-node'

// ─── Trackable Note Schema ───
//
// v1 is read-only (spec §11.1): the editor node carries minimal display-only
// state. Tree-shaped condition data lives on the server-side prediction
// row, fetched via `useGetPredictionQuery` when needed.

export const PredictionStatusSchema = z.enum([
  'pending',
  'correct',
  'incorrect',
  'unresolvable',
  'errored',
  'withdrawn',
])
export type PredictionStatus = z.infer<typeof PredictionStatusSchema>

export const TrackableNoteAttrsSchema = z.object({
  noteId: z.string(),
  // Stable submission id for the backend dedupe / idempotency key. Generated
  // on slash-command insert; persists across edits to the same draft.
  submissionId: z.string(),
  ticker: z.string(),
  direction: z.enum(['bullish', 'bearish']),
  note: z.string(),
  timeframe: z.enum(['eod', 'eow', '1d', '2d', '3d', '1w', '2w']),
  status: PredictionStatusSchema,
  createdAt: z.string(),
  draft: z.boolean(),
  originalText: z.string().optional().default(''),
  // Parsed tree from the LLM parse step; populated when the user confirms a
  // draft. Forwarded to the backend via `useSyncPredictionMutation`.
  tree: TreeNodeSchema.nullable().default(null),
  // ISO timestamp the backend computed for evaluation. Display-only.
  evaluateAt: z.string().nullable().default(null),
})

export type TrackableNoteAttrs = z.infer<typeof TrackableNoteAttrsSchema>

export interface NoteScorecard {
  total: number
  pending: number
  correct: number
  incorrect: number
  hitRate: number | null
}

export interface AggregatePerformance {
  totalPredictions: number
  resolved: number
  pending: number
  correct: number
  incorrect: number
  hitRate: number | null
  recentPredictions: {
    noteId: string
    ticker: string
    direction: 'bullish' | 'bearish'
    note: string
    status: PredictionStatus
    createdAt: string
    evaluatedAt: string | null
    entryId: string
  }[]
}

// ─── Assumption (spec §5) ───

export const AssumptionConfidenceSchema = z.enum(['high', 'medium', 'low'])
export type AssumptionConfidence = z.infer<typeof AssumptionConfidenceSchema>

export const AssumptionSchema = z.object({
  field: z.string(),
  assumed: z.string(),
  alternatives: z.array(z.string()).default([]),
  confidence: AssumptionConfidenceSchema,
})
export type Assumption = z.infer<typeof AssumptionSchema>

// ─── Prediction (backend sync) ───
//
// Mirrors `_to_dict()` in apps/edge-financial-api routers/predictions.py.

export const PredictionFullSchema = z.object({
  id: z.number(),
  submission_id: z.string(),
  note_id: z.string(),
  user_sub: z.string().optional(),
  ticker: z.string(),
  direction: z.enum(['bullish', 'bearish']),
  note: z.string(),
  original_text: z.string().optional().default(''),
  timeframe: z.string(),
  tree: TreeNodeSchema,
  tree_schema_version: z.number().int(),
  status: PredictionStatusSchema,
  evaluate_at: z.string().nullable(),
  evaluated_at: z.string().nullable(),
  resolve_attempts: z.number().int().optional(),
  last_error: z.string().nullable().optional(),
  withdrawn_at: z.string().nullable().optional(),
  eval_snapshot: z.record(z.string(), z.unknown()).nullable().optional(),
  engine_version: z.string().nullable().optional(),
  assumptions: z.array(AssumptionSchema).default([]),
  created_at: z.string(),
  updated_at: z.string(),
})

export type PredictionFull = z.infer<typeof PredictionFullSchema>

// ─── Aggregate track-record stats (GET /predictions/stats) ───
//
// Mirrors `aggregate_stats()` in
// apps/edge-financial-api/edge_financial_api/services/prediction_service.py.
// Pending + withdrawn are always present so the denominator can't be hidden;
// `hit_rate` is null below `min_n` resolved calls (spec §1b/§1c).

export const PredictionStatBucketSchema = z.object({
  pending: z.number().int(),
  correct: z.number().int(),
  incorrect: z.number().int(),
  unresolvable: z.number().int(),
  errored: z.number().int(),
  withdrawn: z.number().int(),
  resolved: z.number().int(),
  hit_rate: z.number().nullable(),
})
export type PredictionStatBucket = z.infer<typeof PredictionStatBucketSchema>

export const PredictionStatsSchema = z.object({
  overall: PredictionStatBucketSchema,
  by_ticker: z.array(PredictionStatBucketSchema.extend({ ticker: z.string() })),
  by_timeframe: z.array(PredictionStatBucketSchema.extend({ timeframe: z.string() })),
  min_n: z.number().int(),
})
export type PredictionStats = z.infer<typeof PredictionStatsSchema>

export type { TreeNode }
